import { IRegionRepository } from '../../domain/interfaces/IRegionRepository';
import {
  Region,
  RegionDetail,
  RegionListItemDto,
  CreateRegionCommand,
  UpdateRegionCommand
} from '../../domain/entities/Region';
import { PaginatedResult, PaginationParams } from '../../domain/common/PaginatedResult';
import { UserRole } from '../../domain/value-objects/UserRole';

/**
 * RegionService - Application service for region operations
 *
 * Orchestrates region CRUD and subreddit management operations
 * All operations are admin-only
 */
export class RegionService {
  constructor(private readonly regionRepository: IRegionRepository) {}

  /**
   * Get all regions with pagination (admin only)
   */
  async getAllRegions(
    params?: PaginationParams,
    userRole?: UserRole
  ): Promise<PaginatedResult<RegionListItemDto>> {
    // Only admins can manage regions
    if (userRole && userRole !== 'Admin') {
      throw new Error('Only admins can view regions');
    }

    return this.regionRepository.getAll(params);
  }

  /**
   * Get a single region by ID (admin only)
   * Returns RegionDetail with subAreaCount (not full subAreas array)
   */
  async getRegionById(id: string, userRole: UserRole): Promise<RegionDetail> {
    // Only admins can view region details
    if (userRole !== 'Admin') {
      throw new Error('Only admins can view region details');
    }

    return this.regionRepository.getById(id);
  }

  /**
   * Create a new region (admin only)
   *
   * NOTE: As of Phase 0 (Sprint 008), coordinates/radiusKm are no longer part of Region.
   * Geographic data is managed via SubAreas (separate API endpoints).
   */
  async createRegion(command: CreateRegionCommand, userRole: UserRole): Promise<Region> {
    // Only admins can create regions
    if (userRole !== 'Admin') {
      throw new Error('Only admins can create regions');
    }

    // Validate required fields
    if (!command.regionId || !command.name || !command.country) {
      throw new Error('Missing required fields: regionId, name, country');
    }

    return this.regionRepository.create(command);
  }

  /**
   * Update an existing region (admin only)
   *
   * NOTE: As of Phase 0 (Sprint 008), coordinates/radiusKm are no longer part of Region.
   * Geographic data is managed via SubAreas (separate API endpoints).
   */
  async updateRegion(
    id: string,
    command: UpdateRegionCommand,
    userRole: UserRole
  ): Promise<Region> {
    // Only admins can update regions
    if (userRole !== 'Admin') {
      throw new Error('Only admins can update regions');
    }

    return this.regionRepository.update(id, command);
  }

  /**
   * Delete a region (admin only)
   */
  async deleteRegion(id: string, userRole: UserRole): Promise<void> {
    // Only admins can delete regions
    if (userRole !== 'Admin') {
      throw new Error('Only admins can delete regions');
    }

    await this.regionRepository.delete(id);
  }

  /**
   * Manage subreddits for a region (admin only)
   * Validates subreddit format and uniqueness
   */
  async manageSubreddits(id: string, subreddits: string[], userRole: UserRole): Promise<void> {
    // Only admins can manage subreddits
    if (userRole !== 'Admin') {
      throw new Error('Only admins can manage subreddits');
    }

    // Validate subreddit format
    const validSubreddits = subreddits.map((sub) => {
      const cleaned = sub.trim();

      // Ensure proper r/ prefix
      if (!cleaned.startsWith('r/')) {
        return `r/${cleaned}`;
      }

      return cleaned;
    });

    // Remove duplicates
    const uniqueSubreddits = Array.from(new Set(validSubreddits));

    await this.regionRepository.updateSubreddits(id, uniqueSubreddits);
  }

  /**
   * Toggle region active status (admin only)
   */
  async toggleRegionActive(id: string, userRole: UserRole): Promise<Region> {
    // Only admins can toggle region status
    if (userRole !== 'Admin') {
      throw new Error('Only admins can toggle region status');
    }

    return this.regionRepository.toggleActive(id);
  }

  /**
   * Validate a region configuration before creation/update
   *
   * NOTE: As of Phase 0 (Sprint 008), coordinates/radiusKm are no longer part of Region.
   * Geographic validation is handled by SubArea API endpoints.
   */
  validateRegionConfig(
    regionId: string,
    subreddits: string[]
  ): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate regionId format (lowercase, hyphens only)
    if (!/^[a-z0-9-]+$/.test(regionId)) {
      errors.push('Region ID must contain only lowercase letters, numbers, and hyphens');
    }

    // Validate subreddits
    if (subreddits.length === 0) {
      errors.push('At least one subreddit is required');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}
